/**
 * 患者状态管理 (Pinia Store)
 *
 * @module patient-management/infrastructure/store
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { Patient, PatientSummary } from "@shared/domain/patientTypes";
import { patientRepository } from "../domain/patientRepository";

export const usePatientManagementStore = defineStore(
  "patientManagement",
  () => {
    // ==================== State ====================

    /** 患者列表 */
    const patients = ref<PatientSummary[]>([]);

    /** 显示的患者列表（搜索/过滤后） */
    const displayedPatients = ref<PatientSummary[]>([]);

    /** 当前选中的患者 */
    const currentPatient = ref<Patient | null>(null);

    /** 加载状态 */
    const loading = ref(false);

    /** 搜索关键词 */
    const searchKeyword = ref("");

    /** 最后管理的患者（用于页面恢复） */
    const lastManagedPatient = ref<Patient | null>(null);

    /** 是否还有更多患者未加载（分页追加） */
    const patientsHasMore = ref(false);
    let latestPatientSelectionRequest = 0;

    // ==================== Getters ====================

    /** 是否有患者 */
    const hasPatients = computed(() => patients.value.length > 0);

    /** 患者总数 */
    const patientCount = computed(() => patients.value.length);

    /** 显示的患者数 */
    const displayedCount = computed(() => displayedPatients.value.length);

    /** 是否有当前患者 */
    const hasCurrentPatient = computed(() => currentPatient.value !== null);

    /** 当前患者名称 */
    const currentPatientName = computed(() => currentPatient.value?.name || "");

    /** 当前患者ID（Mongo 文档 id，用于患者表高亮、GET /patients/:id） */
    const currentPatientId = computed(() => currentPatient.value?.id || "");

    /**
     * 与病历集合 patient_id 对齐的标识（通常为业务号 patientId）。
     * GET /medical-records/patient/{id} 按该字段匹配，勿仅用 Mongo id。
     */
    const medicalRecordPatientId = computed(() => {
      const p = currentPatient.value;
      if (!p) return "";
      return (p.patientId || p.id || "") as string;
    });

    /** 患者ID（兼容别名） */
    const patientId = currentPatientId;

    /** 患者显示名称（兼容别名） */
    const patientDisplayName = currentPatientName;

    /** 是否有最后管理的患者 */
    const hasLastManagedPatient = computed(
      () => lastManagedPatient.value !== null,
    );

    // ==================== Actions ====================

    /** 仓储实体 → 列表摘要（兼容旧版 PatientTable 的 patient_id 字段） */
    const toPatientSummary = (p: Patient): PatientSummary =>
      ({
        id: p.id,
        patientId: p.patientId,
        patient_id: p.patientId,
        name: p.name,
        gender: p.gender,
        age: p.age,
        phone: p.phone,
        status: p.status,
      }) as PatientSummary;

    /**
     * 加载患者列表
     */
    async function loadPatients(limit: number = 100): Promise<void> {
      loading.value = true;
      try {
        const result = await patientRepository.getAll(limit, 0);
        patients.value = result.map(toPatientSummary);
        // 后端不返回 total：返回条数达到页长即认为可能还有下一页
        patientsHasMore.value = result.length >= limit;
        displayedPatients.value = [...patients.value];
      } catch (error) {
        console.error("加载患者列表失败:", error);
      } finally {
        loading.value = false;
      }
    }

    /**
     * 加载更多患者（分页追加）。
     *
     * 原先列表硬编码只取 100 条，租户患者超过 100 时后续患者永远不会出现在界面上。
     * 以已加载条数作为 skip 追加下一页；搜索态下 displayedPatients 是搜索结果，
     * 不在此处覆盖，避免把过滤结果冲掉。
     */
    async function loadMorePatients(limit: number = 100): Promise<void> {
      if (loading.value || !patientsHasMore.value) return;
      loading.value = true;
      try {
        const result = await patientRepository.getAll(
          limit,
          patients.value.length,
        );
        if (result.length === 0) {
          patientsHasMore.value = false;
          return;
        }
        patients.value = [...patients.value, ...result.map(toPatientSummary)];
        patientsHasMore.value = result.length >= limit;
        if (!searchKeyword.value.trim()) {
          displayedPatients.value = [...patients.value];
        }
      } catch (error) {
        console.error("加载更多患者失败:", error);
      } finally {
        loading.value = false;
      }
    }

    /**
     * 搜索患者
     */
    async function searchPatients(keyword: string): Promise<PatientSummary[]> {
      searchKeyword.value = keyword;

      if (!keyword.trim()) {
        displayedPatients.value = [...patients.value];
        return displayedPatients.value;
      }

      loading.value = true;
      try {
        const results = await patientRepository.search(keyword);
        // 添加 patient_id 字段兼容旧版组件
        const mappedResults = results.map((p) => ({
          ...p,
          patient_id: p.patientId,
        }));
        displayedPatients.value = mappedResults;
        return mappedResults;
      } catch (error) {
        console.error("搜索患者失败:", error);
        return [];
      } finally {
        loading.value = false;
      }
    }

    /**
     * 选择患者
     */
    async function selectPatient(patientId: string): Promise<Patient | null> {
      const requestId = ++latestPatientSelectionRequest;
      loading.value = true;
      try {
        const patient = await patientRepository.getById(patientId);
        if (requestId !== latestPatientSelectionRequest) return null;
        if (patient) {
          currentPatient.value = patient;
          lastManagedPatient.value = patient;
          saveLastManagedPatient(patient);
        }
        return patient;
      } catch (error) {
        if (requestId !== latestPatientSelectionRequest) return null;
        console.error("选择患者失败:", error);
        return null;
      } finally {
        if (requestId === latestPatientSelectionRequest) {
          loading.value = false;
        }
      }
    }

    /**
     * 设置当前患者（直接设置，不从服务器获取）
     */
    function setCurrentPatient(patient: Patient | null): void {
      currentPatient.value = patient;
      if (patient) {
        lastManagedPatient.value = patient;
        saveLastManagedPatient(patient);
      }
    }

    /**
     * 清空当前患者
     */
    function clearCurrentPatient(): void {
      currentPatient.value = null;
    }

    /**
     * 主档抽屉保存成功后同步：列表（新建置顶、编辑原位替换）与当前患者
     */
    function applySavedPatient(patient: Patient): void {
      const summary = {
        id: patient.id,
        patientId: patient.patientId,
        patient_id: patient.patientId, // 兼容旧版 PatientTable 组件
        name: patient.name,
        gender: patient.gender,
        age: patient.age,
        phone: patient.phone,
        status: patient.status,
      } as PatientSummary & { patient_id?: string };
      for (const list of [patients, displayedPatients]) {
        const index = list.value.findIndex((p) => p.id === patient.id);
        if (index === -1) list.value.unshift({ ...summary });
        else list.value[index] = { ...list.value[index], ...summary };
      }
      setCurrentPatient(patient);
    }

    /**
     * 删除患者
     */
    async function deletePatient(id: string): Promise<boolean> {
      loading.value = true;
      try {
        const success = await patientRepository.delete(id);
        if (success) {
          patients.value = patients.value.filter((p) => p.id !== id);
          displayedPatients.value = displayedPatients.value.filter(
            (p) => p.id !== id,
          );

          if (currentPatient.value?.id === id) {
            currentPatient.value = null;
          }
        }
        return success;
      } catch (error) {
        console.error("删除患者失败:", error);
        return false;
      } finally {
        loading.value = false;
      }
    }

    /**
     * 重置显示列表
     */
    function resetDisplayedPatients(): void {
      displayedPatients.value = [...patients.value];
      searchKeyword.value = "";
    }

    /**
     * 保存最后管理的患者到 localStorage
     *
     * 安全要求：localStorage 为明文持久化存储，禁止落盘手机号、身份证、
     * 住址、紧急联系人等敏感 PII；只保留恢复"上次管理患者"所需的最小字段，
     * 详情在选中后通过 API 重新拉取。
     */
    function saveLastManagedPatient(patient: Patient): void {
      try {
        const minimal = {
          id:
            (patient as Record<string, any>).id ||
            (patient as Record<string, any>)._id ||
            "",
          patient_id:
            (patient as Record<string, any>).patient_id ||
            (patient as Record<string, any>).patientId ||
            "",
          name: patient.name || "",
          gender: (patient as Record<string, any>).gender || "",
          age: (patient as Record<string, any>).age ?? null,
        };
        localStorage.setItem("pm_lastManagedPatient", JSON.stringify(minimal));
      } catch (error) {
        console.error("保存患者信息失败:", error);
      }
    }

    /**
     * 从 localStorage 加载最后管理的患者
     */
    function loadLastManagedPatient(): Patient | null {
      try {
        const saved = localStorage.getItem("pm_lastManagedPatient");
        if (saved) {
          const patient = JSON.parse(saved) as Patient;
          lastManagedPatient.value = patient;
          return patient;
        }
        return null;
      } catch (error) {
        console.error("加载患者信息失败:", error);
        return null;
      }
    }

    /**
     * 清除最后管理的患者
     */
    function clearLastManagedPatient(): void {
      lastManagedPatient.value = null;
      try {
        localStorage.removeItem("pm_lastManagedPatient");
      } catch (error) {
        console.error("清除患者记录失败:", error);
      }
    }

    /**
     * 重置所有状态
     */
    function resetAll(): void {
      patients.value = [];
      displayedPatients.value = [];
      currentPatient.value = null;
      searchKeyword.value = "";
      patientsHasMore.value = false;
      loading.value = false;
    }

    return {
      // State
      patients,
      displayedPatients,
      currentPatient,
      loading,
      searchKeyword,
      lastManagedPatient,
      patientsHasMore,

      // Getters
      hasPatients,
      patientCount,
      displayedCount,
      hasCurrentPatient,
      currentPatientName,
      currentPatientId,
      medicalRecordPatientId,
      patientId,
      patientDisplayName,
      hasLastManagedPatient,

      // Actions
      loadPatients,
      loadMorePatients,
      searchPatients,
      selectPatient,
      setCurrentPatient,
      clearCurrentPatient,
      applySavedPatient,
      deletePatient,
      resetDisplayedPatients,
      loadLastManagedPatient,
      clearLastManagedPatient,
      resetAll,
    };
  },
);
